import React, { useState, useEffect } from 'react';
import { Star, ChevronDown, ChevronUp, Edit3 } from 'lucide-react';

const ReviewForm = ({ productId, productCategory, onSubmitSuccess, expandByDefault = false }) => {
  const [formData, setFormData] = useState({
    wouldRepurchase: '',
    rating: 0,
    quickTake: '',
    nickname: '',
    skinType: '',
    ageRange: '',
    context: {}
  });
  const [hoverRating, setHoverRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });
  const [isExpanded, setIsExpanded] = useState(expandByDefault);

  // Update expanded state when prop changes
  useEffect(() => {
    setIsExpanded(expandByDefault);
  }, [expandByDefault]);

  const handleRatingClick = (rating) => {
    setFormData({ ...formData, rating });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.wouldRepurchase || !formData.rating) {
      setSubmitMessage({
        type: 'error',
        text: 'Please rate the product and share if you\'d repurchase'
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage({ type: '', text: '' });

    try {
      const { submitReview } = await import('../services/reviewService');
      await submitReview(productId, formData);
      
      setSubmitMessage({
        type: 'success',
        text: '✨ Thank you! Your review helps the community.'
      });
      
      // Reset form
      setFormData({
        wouldRepurchase: '',
        rating: 0,
        quickTake: '',
        nickname: '',
        skinType: '',
        ageRange: '',
        context: {}
      });
      
      // Collapse and refresh after success
      setTimeout(() => {
        setIsExpanded(false);
        if (onSubmitSuccess) onSubmitSuccess();
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting review:', error);
      setSubmitMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error submitting review. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const MAX_CHARS = 1200;
  const charCount = formData.quickTake.length;
  const isTyping = charCount > 0;

  // Collapsed state - engaging CTA
  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="w-full relative overflow-hidden bg-gradient-to-r from-white via-white to-[#fdf9f7] border border-[#e5ddd7] rounded-2xl p-5 hover:border-[#ab3d22]/40 hover:shadow-lg hover:shadow-[#ab3d22]/10 transition-all duration-300 group text-left"
      >
        {/* Subtle shimmer effect on hover */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#ab3d22]/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
        
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="absolute inset-0 bg-[#ab3d22]/20 rounded-xl blur-sm group-hover:blur-md transition-all"></div>
              <div className="relative w-11 h-11 bg-gradient-to-br from-[#ab3d22] to-[#8a2f1a] rounded-xl flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                <Edit3 className="h-5 w-5 text-white" />
              </div>
            </div>
            <div>
              <span className="font-semibold text-gray-900 text-base group-hover:text-[#ab3d22] transition-colors">Write a Review</span>
              <p className="text-sm text-gray-500">Your review helps others make better choices</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-xs text-[#ab3d22] font-medium opacity-0 group-hover:opacity-100 transition-opacity">Let's go</span>
            <ChevronDown className="h-5 w-5 text-gray-400 group-hover:text-[#ab3d22] group-hover:translate-y-0.5 transition-all" />
          </div>
        </div>
      </button>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-2xl border border-[#e5ddd7] shadow-lg shadow-[#ab3d22]/8">
      {/* Background gradient with depth */}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-[#fdf9f7] to-[#f8f3ef]"></div>
      <div className="absolute -top-20 -right-20 w-48 h-48 bg-[#ab3d22]/[0.04] rounded-full blur-3xl"></div>
      <div className="absolute -bottom-16 -left-16 w-40 h-40 bg-[#ab3d22]/[0.03] rounded-full blur-2xl"></div>
      
      {/* Header */}
      <div className="relative bg-gradient-to-r from-white/90 to-white/70 backdrop-blur-sm px-6 py-4 border-b border-[#e8ddd6]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-[#ab3d22]/20 rounded-xl blur-sm"></div>
              <div className="relative w-10 h-10 bg-gradient-to-br from-[#ab3d22] to-[#8a2f1a] rounded-xl flex items-center justify-center shadow-md">
                <Edit3 className="h-5 w-5 text-white" />
              </div>
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-base" style={{fontFamily: 'Poppins, sans-serif'}}>Write a Review</h3>
              <p className="text-sm text-gray-500">Your review helps others make better choices</p>
            </div>
          </div>
          <button
            onClick={() => setIsExpanded(false)}
            className="text-gray-400 hover:text-[#ab3d22] p-2 rounded-xl hover:bg-white/80 transition-all"
            aria-label="Collapse"
          >
            <ChevronUp className="h-5 w-5" />
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="relative p-6 space-y-5">
        {/* Rating - with half stars */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            How would you rate this product? <span className="text-[#ab3d22]">*</span>
          </label>
          <p className="text-xs text-gray-400 mb-2">Tap to rate your overall experience</p>
          <div className="flex gap-0.5 items-center">
            {[1, 2, 3, 4, 5].map((star) => (
              <div key={star} className="relative">
                {/* Left half - 0.5 rating */}
                <button
                  type="button"
                  onClick={() => handleRatingClick(star - 0.5)}
                  onMouseEnter={() => setHoverRating(star - 0.5)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="absolute left-0 w-1/2 h-full z-10 focus:outline-none cursor-pointer"
                  style={{ top: 0 }}
                />
                {/* Right half - full rating */}
                <button
                  type="button"
                  onClick={() => handleRatingClick(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="absolute right-0 w-1/2 h-full z-10 focus:outline-none cursor-pointer"
                  style={{ top: 0 }}
                />
                <Star
                  className={`h-8 w-8 transition-all ${
                    star <= Math.floor(hoverRating || formData.rating)
                      ? 'text-[#ab3d22] fill-current scale-105'
                      : star - 0.5 === (hoverRating || formData.rating)
                      ? 'text-[#ab3d22]'
                      : 'text-gray-300 hover:text-gray-400'
                  }`}
                  style={{
                    fill: star - 0.5 === (hoverRating || formData.rating) 
                      ? 'url(#half-fill)' 
                      : undefined
                  }}
                />
                <svg width="0" height="0" className="absolute">
                  <defs>
                    <linearGradient id="half-fill">
                      <stop offset="50%" stopColor="#ab3d22" />
                      <stop offset="50%" stopColor="#d1d5db" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            ))}
            {formData.rating > 0 && (
              <span className="ml-2 text-sm text-gray-600 font-medium">
                {formData.rating}/5
              </span>
            )}
          </div>
        </div>

        {/* Would Repurchase - compact buttons */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Would you repurchase? <span className="text-[#ab3d22]">*</span>
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, wouldRepurchase: 'yes' })}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg border-2 transition-all text-sm font-medium ${
                formData.wouldRepurchase === 'yes'
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-200 bg-white hover:border-gray-300 text-gray-600'
              }`}
            >
              <span>👍</span>
              <span>Yes</span>
            </button>
            
            <button
              type="button"
              onClick={() => setFormData({ ...formData, wouldRepurchase: 'maybe' })}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg border-2 transition-all text-sm font-medium ${
                formData.wouldRepurchase === 'maybe'
                  ? 'border-amber-500 bg-amber-50 text-amber-700'
                  : 'border-gray-200 bg-white hover:border-gray-300 text-gray-600'
              }`}
            >
              <span>🤔</span>
              <span>Maybe</span>
            </button>
            
            <button
              type="button"
              onClick={() => setFormData({ ...formData, wouldRepurchase: 'no' })}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg border-2 transition-all text-sm font-medium ${
                formData.wouldRepurchase === 'no'
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'border-gray-200 bg-white hover:border-gray-300 text-gray-600'
              }`}
            >
              <span>👎</span>
              <span>No</span>
            </button>
          </div>
        </div>

        {/* About You Section - Elevated card inside form */}
        <div className="bg-white/80 backdrop-blur-sm rounded-xl p-5 space-y-3 border border-[#e8ddd6] shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">About You</span>
            <span className="text-xs text-gray-400">Optional</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            {/* Skin Type */}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Skin Type</label>
              <select
                value={formData.skinType}
                onChange={(e) => setFormData({ ...formData, skinType: e.target.value })}
                className="w-full px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#ab3d22]/20 focus:border-[#ab3d22] transition-colors"
              >
                <option value="">Select...</option>
                <option value="dry">Dry</option>
                <option value="oily">Oily</option>
                <option value="combination">Combination</option>
                <option value="normal">Normal</option>
                <option value="sensitive">Sensitive</option>
              </select>
            </div>

            {/* Age Range */}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Age Range</label>
              <select
                value={formData.ageRange}
                onChange={(e) => setFormData({ ...formData, ageRange: e.target.value })}
                className="w-full px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#ab3d22]/20 focus:border-[#ab3d22] transition-colors"
              >
                <option value="">Select...</option>
                <option value="under-18">Under 18</option>
                <option value="18-24">18-24</option>
                <option value="25-34">25-34</option>
                <option value="35-44">35-44</option>
                <option value="45-54">45-54</option>
                <option value="55+">55+</option>
              </select>
            </div>

            {/* Nickname */}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Display Name</label>
              <input
                type="text"
                value={formData.nickname}
                onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                placeholder="Anonymous"
                maxLength={30}
                className="w-full px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#ab3d22]/20 focus:border-[#ab3d22] transition-colors placeholder:text-gray-300"
              />
            </div>
          </div>
        </div>

        {/* Your Review - Wider textarea */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Your Review <span className="text-gray-400 font-normal text-xs">(optional)</span>
          </label>
          <p className="text-xs text-gray-400 mb-2">Share your routine, results, texture notes — whatever helps others decide</p>
          <div className="relative">
            <textarea
              value={formData.quickTake}
              onChange={(e) => setFormData({ ...formData, quickTake: e.target.value })}
              placeholder="What was your experience with this product? Did it work for your skin type? How long did you use it before seeing results?..."
              maxLength={MAX_CHARS}
              rows={5}
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#ab3d22]/20 focus:border-[#ab3d22] resize-none text-sm transition-all placeholder:text-gray-400 leading-relaxed"
            />
            {/* Character counter - fades in when typing */}
            <div 
              className={`absolute bottom-2.5 right-3 text-xs transition-opacity duration-200 ${
                isTyping ? 'opacity-100' : 'opacity-0'
              } ${charCount > MAX_CHARS * 0.9 ? 'text-amber-500' : 'text-gray-400'}`}
            >
              {charCount}/{MAX_CHARS}
            </div>
          </div>
        </div>

        {/* Submit Message */}
        {submitMessage.text && (
          <div
            className={`p-3 rounded-lg text-sm ${
              submitMessage.type === 'success'
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}
          >
            {submitMessage.text}
          </div>
        )}

        {/* Submit Button - Prominent CTA */}
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-gray-400">
            Posted anonymously
          </p>
          <button
            type="submit"
            disabled={isSubmitting}
            className="relative inline-flex items-center gap-2 bg-gradient-to-r from-[#ab3d22] to-[#8a2f1a] text-white px-5 py-2.5 rounded-xl font-semibold hover:from-[#8a2f1a] hover:to-[#6d2515] transition-all disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#ab3d22] focus:ring-offset-2 text-sm shadow-md shadow-[#ab3d22]/25 hover:shadow-lg hover:shadow-[#ab3d22]/30 hover:-translate-y-0.5"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Submitting...
              </>
            ) : (
              'Submit Review'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReviewForm;
